'use client';

import React, { useState } from 'react';
import { GradientText } from '@/components/atoms/GradientText';
import cn from 'classnames';
import { setHeapPropsClientSide } from '@/helpers/heap/setHeapPropsClientSide';

interface SurveyProps {
	question: string;
}

const Survey = (props: SurveyProps) => {
	const { question } = props;
	const [answered, setAnswered] = useState(false);

	const handleSurvey = (e: React.MouseEvent<HTMLButtonElement>) => {
		setAnswered(true);
		setHeapPropsClientSide({
			[`SurveyQuestion ${question}`]:
				e.currentTarget.dataset.survey || 'No Answer',
		});
	};

	return (
		<div className="mb-8 mt-1 items-center gap-x-10 gap-y-4 rounded-2xl border border-gray-200 bg-slate-50 px-4 py-8 text-center text-slate-600 md:rounded-6xl lg:rounded-3xl lg:px-8 lg:py-10">
			<div className="relative text-balance pb-12 text-2xl font-bold lg:text-4xl">
				<GradientText>{question}</GradientText>
				{/* If answered */}
				<div
					data-view="thank-you"
					data-testid="thankYou"
					className={cn(
						'absolute bottom-0 left-1/2 w-full -translate-x-1/2 transition-opacity duration-300',
						{
							'opacity-0': !answered,
							'opacity-100': answered,
						},
					)}
					aria-hidden={!answered}
				>
					<p className="text-balance text-slate-500">
						Thank you for participating in our survey
					</p>
				</div>
				{/* If not answered */}
				<div
					data-view="survey"
					data-testid="survey"
					className={cn(
						// top is the line-height of the text
						'absolute bottom-0 left-1/2 flex -translate-x-1/2 items-center justify-center gap-9 transition-opacity duration-300',
						{
							'opacity-100': !answered,
							'opacity-0': answered,
						},
					)}
					aria-hidden={answered}
				>
					<button data-survey="Yes" onClick={handleSurvey}>
						<svg
							aria-label="thumb up"
							xmlns="http://www.w3.org/2000/svg"
							viewBox="0 0 512 512"
							className="h-7 w-7 fill-slate-500 hover:fill-neutral-700"
						>
							<path d="M313.4 32.9c26 5.2 42.9 30.5 37.7 56.5l-2.3 11.4c-5.3 26.7-15.1 52.1-28.8 75.2h144c26.5 0 48 21.5 48 48 0 18.5-10.5 34.6-25.9 42.6C497 275.4 504 288.9 504 304c0 23.4-16.8 42.9-38.9 47.1 4.4 7.3 6.9 15.8 6.9 24.9 0 21.3-13.9 39.4-33.1 45.6.7 3.3 1.1 6.8 1.1 10.4 0 26.5-21.5 48-48 48h-97.5c-19 0-37.5-5.6-53.3-16.1l-38.5-25.7C176 420.4 160 390.4 160 358.3V247.1c0-29.2 13.3-56.7 36-75l7.4-5.9c26.5-21.2 44.6-51 51.2-84.2l2.3-11.4c5.2-26 30.5-42.9 56.5-37.7zM32 192h64c17.7 0 32 14.3 32 32v224c0 17.7-14.3 32-32 32H32c-17.7 0-32-14.3-32-32V224c0-17.7 14.3-32 32-32z" />
						</svg>
					</button>
					<button data-survey="No" onClick={handleSurvey}>
						<svg
							aria-label="thumb down"
							xmlns="http://www.w3.org/2000/svg"
							viewBox="0 0 512 512"
							className="h-7 w-7 fill-slate-500 hover:fill-neutral-700"
						>
							<path d="M313.4 479.1c26-5.2 42.9-30.5 37.7-56.5l-2.3-11.4c-5.3-26.7-15.1-52.1-28.8-75.2h144c26.5 0 48-21.5 48-48 0-18.5-10.5-34.6-25.9-42.6C497 236.6 504 223.1 504 208c0-23.4-16.8-42.9-38.9-47.1 4.4-7.3 6.9-15.8 6.9-24.9 0-21.3-13.9-39.4-33.1-45.6.7-3.3 1.1-6.8 1.1-10.4 0-26.5-21.5-48-48-48h-97.5c-19 0-37.5 5.6-53.3 16.1l-38.5 25.7C176 91.6 160 121.6 160 153.7V264.9c0 29.2 13.3 56.7 36 75l7.4 5.9c26.5 21.2 44.6 51 51.2 84.2l2.3 11.4c5.2 26 30.5 42.9 56.5 37.7zM32 384h64c17.7 0 32-14.3 32-32V128c0-17.7-14.3-32-32-32H32c-17.7 0-32 14.3-32 32v224c0 17.7 14.3 32 32 32z" />
						</svg>
					</button>
				</div>
			</div>
		</div>
	);
};

export default Survey;
